import {observer, Popover2, React} from '@api'
import {Icon} from '@blueprintjs/core'
import {AlignType, BoxIcon, DistributeType, Divider, StretchType, ToolButton, useWhiteboardStore} from '@modules/whiteboard'
import {
  FileCopy as CopyIcon, Rotate90DegreesCcw as RotateCounterClockwiseIcon, LockOpen, Lock,
  AspectRatio, Group, ArrowBack, ArrowDownward, ArrowUpward, VerticalAlignBottom, VerticalAlignTop, FormatAlignLeft, FormatAlignRight, FormatAlignCenter,
} from '@material-ui/icons'
import {DMContent} from '../DropdownMenu'
import {Tooltip} from '../Tooltip'
import * as css from './ActionButton.css'

export const ActionButton = observer(function ActionButton() {
    const {areAllSelectedShapesLocked: isAllLocked, areAllSelectedShapesAspectLocked: isAllAspectLocked, areAllSelectedShapesGrouped :isAllGrouped, hasSelection, hasMultipleShapesSelected: hasMultipleSelection, selectedShapes,
      rotate, resetSelectedShapeRotations, distribute, duplicate, toggleAspectRatioLocked, toggleLocked, group, align, stretch, ungroup, moveBackward, moveForward, moveToBack, moveToFront, moveToPage} = useWhiteboardStore(),
      selectedShapesCount = selectedShapes.length,
      hasMultipleShapesSelected = selectedShapesCount > 1,
      hasThreeOrMore = selectedShapesCount > 2
  
  return (
    <Popover2
      interactionKind='click'
      popoverClassName={css.popover}
      content={
        <DMContent sideOffset={16}>
          <>
            <div className={css.row}>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => duplicate()}>
                <Tooltip label="Duplicate" kbd={`#D`}>
                  <Icon icon='duplicate' />
                </Tooltip>
              </ToolButton>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => rotate()}>
                <Tooltip label="Rotate">
                  <RotateCounterClockwiseIcon />
                </Tooltip>
              </ToolButton>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => toggleLocked()}>
                <Tooltip label="Toggle Locked" kbd={`#L`}>
                  {isAllLocked ? <Lock /> : <LockOpen />}
                </Tooltip>
              </ToolButton>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => toggleAspectRatioLocked()}>
                <Tooltip label="Toggle Aspect Ratio Lock">
                  {isAllAspectLocked ? <AspectRatio /> : <BoxIcon />}
                </Tooltip>
              </ToolButton>
              <ToolButton
                variant="icon"
                disabled={!hasSelection || (!isAllGrouped && !hasMultipleSelection)}
                onClick={() => group()}
              >
                <Tooltip label="Group" kbd={`#G`}>
                  <Group />
                </Tooltip>
              </ToolButton>
            </div>
            <div className={css.row}>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => moveToBack()}>
                <Tooltip label="Move to Back" kbd={`#⇧[`}>
                  <VerticalAlignBottom />
                </Tooltip>
              </ToolButton>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => moveBackward()}>
                <Tooltip label="Move Backward" kbd={`#[`}>
                  <ArrowDownward />
                </Tooltip>
              </ToolButton>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => moveForward()}>
                <Tooltip label="Move Forward" kbd={`#]`}>
                  <ArrowUpward />
                </Tooltip>
              </ToolButton>
              <ToolButton variant="icon" disabled={!hasSelection} onClick={() => moveToFront()}>
                <Tooltip label="More to Front" kbd={`#⇧]`}>
                  <VerticalAlignTop />
                </Tooltip>
              </ToolButton>
              {/*<ToolButton variant="icon" disabled={!hasSelection} onClick={() => deleteShapes()}>*/}
              {/*  <Tooltip label="Delete" kbd="⌫">*/}
              {/*    <TrashIcon />*/}
              {/*  </Tooltip>*/}
              {/*</ToolButton>*/}
              <ToolButton variant="icon" disabled={!hasSelection} onClick={resetSelectedShapeRotations}>
                <Tooltip label="Reset Angle">
                  <Icon icon='image-rotate-right' />
                </Tooltip>
              </ToolButton>
            </div>
            <Divider />
            <div className={css.row}>
              <AlignLeft />
              <CenterHorizontal />
              <AlignRight />
              <StretchHorizontal />
              <DistributeHorizontal />
            </div>
            <div className={css.row}>
              <AlignTop />
              <CenterVertical />
              <AlignBottom />
              <StretchVertical />
              <DistributeVertical />
            </div>
          </>
        </DMContent>
      }
    >
      <ToolButton variant="circle">
        <Icon icon={'more'} />
      </ToolButton>
    </Popover2>
  )
})

export const AlignLeft = observer(function AlignLeft() {
    const {hasMultipleShapesSelected, align} = useWhiteboardStore()
    
    return (<Tooltip label={'Align Left'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => align('left')}>
        <FormatAlignLeft />
      </ToolButton>
    </Tooltip>)
})

export const CenterHorizontal = observer(function CenterHorizontal() {
  const {hasMultipleShapesSelected, align} = useWhiteboardStore()
  
  return (
    <Tooltip label={'Center Horizontal'}>
    <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => align('centerHorizontal')}>
      <FormatAlignCenter />
    </ToolButton>
    </Tooltip>
  )
})
export const CenterVertical = observer(function CenterVertical() {
  const {hasMultipleShapesSelected, align} = useWhiteboardStore()

  return (
    <Tooltip label={'Center Vertical'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => align('centerVertical')}>
        <Icon icon={'alignment-vertical-center'} />
      </ToolButton>
    </Tooltip>
  )
})

export const AlignRight = observer(function AlignRight() {
  const {hasMultipleShapesSelected, align} = useWhiteboardStore()

  return (<Tooltip label={'Align Right'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => align('right')}>
        <FormatAlignRight />
      </ToolButton>
    </Tooltip>
  )
})

export const StretchHorizontal = observer(function StretchHorizontal() {
  const {hasMultipleShapesSelected, stretch} = useWhiteboardStore()

  return (<Tooltip label={'Stretch Horizontal'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => stretch('horizontal')}>
        <Icon icon={'arrows-horizontal'} />
      </ToolButton>
    </Tooltip>
  )
})

export const DistributeHorizontal = observer(function DistributeHorizontal() {
  const {hasThreeOrMoreShapesSelected, distribute} = useWhiteboardStore()

  return (<Tooltip label={'Distribute Horizontal'}>
      <ToolButton variant="icon" disabled={!hasThreeOrMoreShapesSelected} onClick={() => distribute('horizontal')}>
        <Icon icon={'horizontal-distribution'} />
      </ToolButton>
    </Tooltip>
  )
})

export const DistributeVertical = observer(function DistributeVertical() {
  const {hasThreeOrMoreShapesSelected, distribute} = useWhiteboardStore()

  return (<Tooltip label={'Distribute Vertical'}>
      <ToolButton variant="icon" disabled={!hasThreeOrMoreShapesSelected} onClick={() => distribute('vertical')}>
        <Icon icon={'vertical-distribution'} />
      </ToolButton>
    </Tooltip>
  )
})

export const StretchVertical = observer(function StretchVertical() {
  const {hasMultipleShapesSelected, stretch} = useWhiteboardStore()

  return (<Tooltip label={'Stretch Vertical'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => stretch('vertical')}>
        <Icon icon={'arrows-vertical'} />
      </ToolButton>
    </Tooltip>
  )
})

export const AlignTop = observer(function AlignTop() {
  const {hasMultipleShapesSelected, align} = useWhiteboardStore()

  return (<Tooltip label={'Align Top'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => align('top')}>
        <Icon icon={'alignment-top'} />
      </ToolButton>
    </Tooltip>
  )
})

export const AlignVerticalCenter = observer(function AlignVerticalCenter() {
  const {hasMultipleShapesSelected, align} = useWhiteboardStore()

  return (<Tooltip label={'Align Vertical Center'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => align('centerVertical')}>
        <Icon icon={'alignment-vertical-center'} />
      </ToolButton>
  </Tooltip>
  )
})

export const AlignBottom = observer(function AlignBottom() {
  const {hasMultipleShapesSelected, align} = useWhiteboardStore()

  return (<Tooltip label={'Align Bottom'}>
      <ToolButton variant="icon" disabled={!hasMultipleShapesSelected} onClick={() => align('bottom')}>
        <Icon icon={'alignment-bottom'} />
      </ToolButton>
    </Tooltip>
  )
})


// WEBPACK FOOTER //
// ./src/modules/whiteboard/components/ToolsPanel/ActionButton.tsx